/**
 * Bereiche + Views + Groups — statische Navigation-Definition (Phase 3.5 §5).
 *
 * 2-stufige Sub-Sidebar-Struktur:
 *   - Bereiche (Rail-Items) — z.B. "Vertrieb", "Finance", "HR"
 *   - Groups (Sub-Sidebar Top-Level) — z.B. "Calls", "Meetings", "Follow Ups"
 *     · Klick auf Group-Header navigiert zur Default-Sub-View (Option A)
 *     · Active-Highlight wenn current view in der Group liegt
 *   - Views (Sub-Tabs unter Group) — z.B. "Overview", "Bilanz", "Pflege"
 *
 * Standalone-Items (z.B. Übersicht, Pflegelücken, Ziele):
 *   Group mit genau 1 View → renderbar als Single-Link ohne Sub-Tab-Block.
 *
 * Routes bleiben flat (Option 1): `/caller`, `/meetings/kalender`,
 * `/pflegeluecken` — Sub-Sidebar-Anzeige ist gruppiert, URLs unverändert.
 */

import type { UserContext } from "../api/types";

export type AktiveFunktion = "Caller" | "Host_MT" | "Host_FU";

export type ViewDef = {
  key: string;
  label: string;
  route: string;
  requiresAktiv?: AktiveFunktion[];
  adminOnly?: boolean;
  comingSoon?: boolean;
  /**
   * Wenn true: View ist nicht als Sub-Tab in der Sidebar sichtbar, sondern
   * nur als Group-Header-Klick-Ziel (Default-View). Spart Sub-Sidebar-Höhe,
   * vermeidet doppelte "Overview"-Einträge. View bleibt aber in
   * flattenedViews/findViewForRoute, damit Routes auflösbar bleiben.
   */
  hideAsSubTab?: boolean;
};

export type GroupDef = {
  key: string;
  label: string;
  iconKey: string;
  /** Route bei Group-Header-Klick. Default: erste nicht-coming-soon View. */
  defaultRoute?: string;
  views: ViewDef[];
  /** Group ganz versteckt für non-Admin/non-BL. */
  adminOnly?: boolean;
  /** Group nur für User mit dieser Funktion sichtbar. */
  requiresAktiv?: AktiveFunktion[];
  /** Group noch nicht freigeschaltet (komplett). */
  comingSoon?: boolean;
  /**
   * Wenn true: Group ist eine Bereichs-Einstellungs-Gruppe. Sie wird NICHT in
   * der normalen Nav-Liste der Sub-Sidebar gerendert, sondern über ein Zahnrad
   * im Sub-Sidebar-Head + eine Tab-Leiste in der Container-View erreicht.
   * Views bleiben in flattenedViews/findViewForRoute auflösbar.
   */
  isSettings?: boolean;
};

export type BereichDef = {
  slug: string;
  label: string;
  short: string;
  iconKey: string;
  /** Hex-Farbe für Hub-Bereich-Tile-Badge. */
  color: string;
  requiresBereich?: string;
  /** Sichtbar wenn isAdmin ODER User in ≥1 Bereich Bereichsleiter ist (Admin-Bereich 3.7). */
  requiresAdminOrBl?: boolean;
  /** Sichtbar wenn isAdmin ODER Bereichsleiter DIESES Bereichs (Slug) — z.B. HR-Manage. */
  requiresBl?: string;
  /** Nur für isAdmin sichtbar (admin-only Bereich wie DQ — passt zum admin-only Backend). */
  requiresAdmin?: boolean;
  /** Bereich ist insgesamt noch nicht freigeschaltet. */
  comingSoon?: boolean;
  /** 2-stufige Struktur (Vertrieb). Wenn vorhanden → groups rendern, sonst views. */
  groups?: GroupDef[];
  /** Flat-Views (Bereiche ohne 2-stufige Struktur — Hub, Mein-Bereich, ...). */
  views: ViewDef[];
};

// =============================================================================
// 9 Apps-Script-Bereiche (Hub + 8 Bereiche), Stand 2026-05-24
// =============================================================================

export const BEREICHE: BereichDef[] = [
  {
    slug: "hub",
    label: "Hub",
    short: "Hub",
    iconKey: "home",
    color: "#D1FD51",
    views: [{ key: "hub", label: "Hub", route: "/" }]
  },
  {
    slug: "meinbereich",
    label: "Mein Bereich",
    short: "Ich",
    iconKey: "user-circle",
    color: "#6366f1",
    groups: [
      { key: "profil", label: "Profil", iconKey: "user-circle", views: [{ key: "profil", label: "Profil", route: "/profil" }] },
      { key: "konto", label: "Konto & Sicherheit", iconKey: "shield-check", views: [{ key: "konto", label: "Konto & Sicherheit", route: "/konto" }] },
      { key: "urlaub", label: "Urlaub", iconKey: "palmtree", views: [{ key: "urlaub", label: "Urlaub", route: "/urlaub" }] },
      { key: "stunden", label: "Stunden", iconKey: "clock", views: [{ key: "stunden", label: "Stunden", route: "/stunden" }] },
      { key: "krank", label: "Krank", iconKey: "heart-pulse", views: [{ key: "krank", label: "Krank", route: "/krank" }] }
    ],
    views: []
  },
  {
    slug: "vertrieb",
    label: "Vertrieb",
    short: "Vertrieb",
    iconKey: "trending-up",
    color: "#10b981",
    requiresBereich: "vertrieb",
    groups: [
      {
        key: "uebersicht",
        label: "Vertriebs-Übersicht",
        iconKey: "layout-grid",
        views: [
          { key: "uebersicht", label: "Übersicht", route: "/uebersicht" }
        ]
      },
      {
        key: "pflegeluecken-group",
        label: "Pflegelücken",
        iconKey: "alert-triangle",
        views: [
          { key: "pflegeluecken", label: "Pflegelücken", route: "/pflegeluecken" }
        ]
      },
      {
        key: "ziele",
        label: "Ziele",
        iconKey: "target",
        views: [
          { key: "ziele", label: "Ziele", route: "/ziele" }
        ]
      },
      {
        key: "calls",
        label: "Calls",
        iconKey: "phone",
        views: [
          { key: "caller", label: "Übersicht", route: "/caller", hideAsSubTab: true },
          { key: "caller-bilanz", label: "Bilanz", route: "/caller/bilanz" },
          { key: "caller-pflege", label: "Pflege", route: "/caller/pflege", requiresAktiv: ["Caller"] }
        ]
      },
      {
        key: "meetings",
        label: "Meetings",
        iconKey: "calendar-check",
        views: [
          { key: "meetings", label: "Übersicht", route: "/meetings", hideAsSubTab: true },
          { key: "meetings-kalender", label: "Kalender", route: "/meetings/kalender" },
          { key: "meetings-slots", label: "Slots", route: "/meetings/slots" }
        ]
      },
      {
        key: "fu",
        label: "Follow Ups",
        iconKey: "repeat",
        views: [
          { key: "fu", label: "Übersicht", route: "/fu", hideAsSubTab: true },
          { key: "fu-kalender", label: "Kalender", route: "/fu/kalender" }
        ]
      },
      {
        key: "closings",
        label: "Closings",
        iconKey: "check-circle",
        views: [
          { key: "closings", label: "Übersicht", route: "/closings", hideAsSubTab: true },
          { key: "closings-liste", label: "Liste", route: "/closings/liste" }
        ]
      },
      {
        key: "funnel",
        label: "Funnel",
        iconKey: "bar-chart-3",
        views: [
          { key: "funnel-status", label: "Status", route: "/funnel/status", hideAsSubTab: true },
          { key: "funnel-performance", label: "Performance", route: "/funnel/performance" }
        ]
      },
      {
        key: "vertriebshistorie-group",
        label: "Vertriebshistorie",
        iconKey: "history",
        views: [
          { key: "vertriebshistorie", label: "Vertriebshistorie", route: "/vertriebshistorie" }
        ]
      },
      {
        // Bereichs-Einstellungen (Phase 7.4, D3): adminOnly=true ⇒ via
        // visibleGroupsForUser (isBlOrAdmin) sichtbar für BL Vertrieb + Admin,
        // NICHT für normale Caller/Hosts. Backend gated identisch
        // (mayEditBereichSettings(auth, "vertrieb")).
        key: "vertrieb-einstellungen",
        label: "Einstellungen",
        iconKey: "settings",
        adminOnly: true,
        isSettings: true,
        // Single-Link in der Sub-Sidebar: Group-Header-Klick → Container-Route
        // /vertrieb/einstellungen (Tab-Container). Alle Sub-Views hideAsSubTab,
        // damit KEINE Sub-Tab-Liste in der Sidebar erscheint (Nav-Umbau 2026-06-04).
        defaultRoute: "/vertrieb/einstellungen",
        views: [
          // Bare Container-Route (ohne Sub) → erster Tab. hideAsSubTab, damit es
          // nur die Bereich-Auflösung (findViewForRoute) füttert.
          { key: "vertrieb-einstellungen", label: "Einstellungen", route: "/vertrieb/einstellungen", hideAsSubTab: true },
          { key: "vertrieb-roster", label: "Caller & Hosts", route: "/vertrieb/einstellungen/roster", hideAsSubTab: true },
          { key: "vertrieb-slot-verfuegbarkeit", label: "Slot-Verfügbarkeit", route: "/vertrieb/einstellungen/slot-verfuegbarkeit", hideAsSubTab: true },
          { key: "vertrieb-schwellen", label: "Pflege-Schwellen", route: "/vertrieb/einstellungen/schwellen", hideAsSubTab: true },
          { key: "vertrieb-slack-templates", label: "Benachrichtigungen", route: "/vertrieb/einstellungen/slack-templates", hideAsSubTab: true },
          { key: "vertrieb-referenzen", label: "Lead-Status & Outcomes", route: "/vertrieb/einstellungen/referenzen", hideAsSubTab: true },
          { key: "vertrieb-lost-grund", label: "Lost-Gründe", route: "/vertrieb/einstellungen/lost-grund", hideAsSubTab: true }
        ]
      }
    ],
    views: [] // wird im Code aus groups gefüttert (flattenedViews)
  },
  {
    slug: "kundenbetreuung",
    label: "Kundenbetreuung",
    short: "KB",
    iconKey: "life-buoy",
    color: "#06b6d4",
    requiresBereich: "kb",
    groups: [
      {
        // Direkt-Link (Single-View-Gruppe) — kein aufklappendes Untermenü mehr.
        key: "kb-dashboard-group",
        label: "Dashboard",
        iconKey: "layout-grid",
        views: [
          { key: "kb-dashboard", label: "Dashboard", route: "/kb/dashboard" }
        ]
      },
      {
        // P13c: KB-native Bestands-/Churn-Kennzahlen + Monatsreihe (Debug_Churn12) —
        // eigene Nav-Gruppe (Direkt-Link) statt Unterpunkt unter „Dashboard".
        key: "kb-bestand-group",
        label: "Bestand & Churn",
        iconKey: "trending-down",
        views: [
          { key: "kb-bestand", label: "Bestand & Churn", route: "/kb/bestand" }
        ]
      },
      {
        key: "kb-vereine-group",
        label: "Vereine",
        iconKey: "building-2",
        views: [
          { key: "kb-vereine", label: "Vereine", route: "/kb" }
        ]
      },
      {
        key: "kb-erfassung-group",
        label: "Gespräche",
        iconKey: "phone-call",
        views: [
          { key: "kb-erfassung", label: "Gespräch erfassen", route: "/kb/erfassung" }
        ]
      },
      {
        // P8: priorisierte Betreuungsliste („wen anrufen") + Betreuungs-KPI je Verein.
        key: "kb-betreuung-group",
        label: "Betreuung",
        iconKey: "trending-up",
        views: [
          { key: "kb-betreuung", label: "Betreuungsliste", route: "/kb/betreuung" }
        ]
      },
      {
        // Paket 3: Aufgaben-Engine — „was habe ich offen" (pro Phase, zugewiesen).
        key: "kb-aufgaben-group",
        label: "Aufgaben",
        iconKey: "list-checks",
        views: [
          { key: "kb-aufgaben", label: "Aufgaben", route: "/kb/aufgaben" }
        ]
      },
      {
        // P11: Zentralkalender — Onboarding-/Einführungstermine über alle Vereine.
        key: "kb-kalender-group",
        label: "Kalender",
        iconKey: "calendar-days",
        views: [
          { key: "kb-kalender", label: "Kalender", route: "/kb/kalender" }
        ]
      },
      {
        key: "kb-kontakte-group",
        label: "Kontakte",
        iconKey: "users",
        views: [
          { key: "kb-kontakte", label: "Kontakte & Trainer", route: "/kb/kontakte" }
        ]
      },
      {
        key: "kb-sonderaktionen-group",
        label: "Sonderaktionen",
        iconKey: "sparkles",
        views: [
          { key: "kb-sonderaktionen", label: "Sonderaktionen", route: "/kb/sonderaktionen" }
        ]
      },
      {
        // Einstellungen (P0b): für alle KB-User sichtbar; Bearbeiten backend-gegated
        // (Admin/Bereichsleiter), sonst read-only. Kein isSettings-Container (v1).
        key: "kb-einstellungen-group",
        label: "Einstellungen",
        iconKey: "settings",
        views: [
          { key: "kb-einstellungen", label: "Einstellungen", route: "/kb/einstellungen" }
        ]
      }
    ],
    views: []
  },
  {
    slug: "finance",
    label: "Finance",
    short: "Fin",
    iconKey: "wallet",
    color: "#f59e0b",
    // F0: Bereich live. Jeder MA mit Bereich „finance" sieht ihn (inkl. GF read-only);
    // Bearbeiten/Setup ist backend-gegated (Bereichsleiter „finance"/Admin).
    requiresBereich: "finance",
    groups: [
      {
        key: "finance-uebersicht-group",
        label: "Übersicht",
        iconKey: "layout-grid",
        views: [
          { key: "finance-uebersicht", label: "Übersicht", route: "/finance" }
        ]
      },
      {
        // F1: Finance Master in der App — Vereinsabo (Preis live aus Stripe, Soll/IST).
        key: "finance-vereinsabo-group",
        label: "Vereinsabo",
        iconKey: "wallet",
        views: [
          { key: "finance-vereinsabo", label: "Vereinsabos", route: "/finance/vereinsabo" }
        ]
      },
      {
        // F2: IST-Layer / Buchhaltung — Konten + kategorisiertes Kontobuch.
        key: "finance-buchhaltung-group",
        label: "Buchhaltung",
        iconKey: "list",
        views: [
          { key: "finance-konten", label: "Konten", route: "/finance/konten" },
          { key: "finance-buchhaltung", label: "Kontobuch", route: "/finance/buchhaltung" }
        ]
      },
      {
        // F3: Business Case SOLL/PLAN + IST — editierbare Annahmen, Plan, Soll-Ist.
        key: "finance-plan-group",
        label: "Business Case",
        iconKey: "trending-up",
        views: [
          { key: "finance-plan", label: "Plan-Output", route: "/finance/plan" },
          { key: "finance-plan-annahmen", label: "Annahmen", route: "/finance/plan/annahmen" },
          { key: "finance-soll-ist", label: "Soll-Ist", route: "/finance/soll-ist" }
        ]
      },
      {
        // F4: Ziele + Reporting / Gesellschafter-One-Pager.
        key: "finance-reporting-group",
        label: "Ziele & Reporting",
        iconKey: "target",
        views: [
          { key: "finance-ziele", label: "Ziele", route: "/finance/ziele" },
          { key: "finance-reporting", label: "One-Pager", route: "/finance/reporting" }
        ]
      },
      {
        // F0: geteilte Einnahmen-/Kostentaxonomie (TAX_KATEGORIEN, BC-Spiegel).
        key: "finance-taxonomie-group",
        label: "Taxonomie",
        iconKey: "list",
        views: [
          { key: "finance-taxonomie", label: "Kategorien", route: "/finance/taxonomie" }
        ]
      }
    ],
    views: []
  },
  {
    slug: "hr",
    label: "HR",
    short: "HR",
    iconKey: "users",
    color: "#ec4899",
    requiresBl: "hr", // Admin oder HR-Leiter (Bereichsleiter "hr")
    // Jede View als eigener Eintrag (Single-View-Gruppen → eigenes Icon, keine
    // Sammel-Gruppen). Justin will jede View einzeln in der Sidebar (2026-06-16).
    groups: [
      { key: "hr-uebersicht", label: "Übersicht", iconKey: "layout-grid", views: [{ key: "hr-uebersicht", label: "Übersicht", route: "/hr" }] },
      { key: "hr-urlaub", label: "Urlaub", iconKey: "palmtree", views: [{ key: "hr-urlaub", label: "Urlaub", route: "/hr/urlaub" }] },
      { key: "hr-zeit", label: "Zeiten", iconKey: "clock", views: [{ key: "hr-zeit", label: "Zeiten", route: "/hr/zeit" }] },
      { key: "hr-abwesenheiten", label: "Abwesenheiten", iconKey: "heart-pulse", views: [{ key: "hr-abwesenheiten", label: "Abwesenheiten", route: "/hr/abwesenheiten" }] },
      { key: "hr-mitarbeiter", label: "Mitarbeiter", iconKey: "users", views: [{ key: "hr-mitarbeiter", label: "Mitarbeiter", route: "/hr/mitarbeiter" }] },
      { key: "hr-einstellungen", label: "Einstellungen", iconKey: "settings", views: [{ key: "hr-einstellungen", label: "Einstellungen", route: "/hr/einstellungen" }] }
    ],
    views: []
  },
  {
    slug: "app",
    label: "App",
    short: "App",
    iconKey: "smartphone",
    color: "#8b5cf6",
    comingSoon: true,
    views: []
  },
  {
    slug: "dq",
    label: "Data-Quality",
    short: "DQ",
    iconKey: "shield-check",
    color: "#22c55e",
    requiresAdmin: true, // DQ = reines Admin-Tool (Justin 2026-05-30), passt zum admin-only Backend
    views: [{ key: "dq", label: "DQ-Übersicht", route: "/dq", adminOnly: true }]
  },
  {
    slug: "investor",
    label: "Investor",
    short: "Inv",
    iconKey: "presentation",
    color: "#64748b",
    comingSoon: true,
    views: []
  },
  {
    slug: "admin",
    label: "Admin",
    short: "Adm",
    iconKey: "settings",
    color: "#94a3b8",
    requiresAdminOrBl: true,
    // 2-stufig (Gruppe → Sub-View, analog Vertrieb): Header-Klick → Haupt-View
    // (hideAsSubTab), darunter die Sub-Tabs. (Justin 2026-06-03)
    groups: [
      {
        key: "admin-mitarbeiter",
        label: "Mitarbeiter",
        iconKey: "user-circle",
        views: [
          { key: "admin-ma", label: "Verwaltung", route: "/admin/ma", hideAsSubTab: true },
          { key: "admin-berechtigungen", label: "Berechtigungen", route: "/admin/berechtigungen" },
          { key: "admin-zugriffsanfragen", label: "Zugriffsanfragen", route: "/admin/zugriffsanfragen", adminOnly: true }
        ]
      },
      {
        key: "admin-system",
        label: "System",
        iconKey: "settings",
        adminOnly: true,
        views: [
          { key: "admin-settings", label: "Einstellungen", route: "/admin/settings", hideAsSubTab: true },
          { key: "admin-system-format", label: "Datumsformat", route: "/admin/system/datumsformat" },
          { key: "admin-feiertage", label: "Feiertage", route: "/admin/system/feiertage" },
          { key: "admin-sheets", label: "Sheet-Inventar", route: "/admin/sheets" }
        ]
      },
      {
        key: "admin-integrationen",
        label: "Integrationen",
        iconKey: "external-link",
        adminOnly: true,
        views: [
          { key: "admin-integrations", label: "Übersicht", route: "/admin/system/integrationen", hideAsSubTab: true }
        ]
      },
      {
        key: "admin-daten",
        label: "Companies-Cache",
        iconKey: "database",
        adminOnly: true,
        views: [
          { key: "admin-companies-cache", label: "Companies-Cache", route: "/admin/system/companies-cache", hideAsSubTab: true }
        ]
      },
      {
        key: "admin-protokoll",
        label: "Protokoll",
        iconKey: "history",
        adminOnly: true,
        views: [
          { key: "admin-audit", label: "Audit-Log", route: "/admin/audit", hideAsSubTab: true }
        ]
      }
      // Slack-Texte + Lost-Gründe: re-homed nach Vertrieb → Einstellungen (D3, 7.4)
    ],
    views: []
  }
];

// =============================================================================
// Filter-Helpers
// =============================================================================

/** Liefert flat-Views eines Bereichs (egal ob aus groups oder direkt views). */
export function flattenedViews(b: BereichDef): ViewDef[] {
  if (b.groups && b.groups.length > 0) {
    return b.groups.flatMap((g) => g.views);
  }
  return b.views;
}

/** Returnt ALLE Bereiche (auch coming-soon — die werden disabled gerendert). */
export function allBereicheForRail(): BereichDef[] {
  return BEREICHE;
}

/** Returnt nur die LIVE-zugänglichen Bereiche für den User (klickbar). */
export function liveBereicheForUser(user: UserContext): BereichDef[] {
  const allowed = new Set(user.allowedBereiche.map((b) => b.toLowerCase()));
  const wildcard = allowed.has("*");
  return BEREICHE.filter((b) => {
    if (b.comingSoon) return false;
    if (b.requiresAdmin) return user.isAdmin;
    if (b.requiresAdminOrBl) return user.isAdmin || isAnyBl(user);
    if (b.requiresBl) return user.isAdmin || isBlOfBereich(user, b.requiresBl);
    if (!b.requiresBereich) return true;
    if (wildcard) return true;
    return allowed.has(b.requiresBereich.toLowerCase());
  });
}

export function isBereichLiveForUser(b: BereichDef, user: UserContext): boolean {
  if (b.comingSoon) return false;
  if (b.requiresAdmin) return user.isAdmin;
  if (b.requiresAdminOrBl) return user.isAdmin || isAnyBl(user);
  if (b.requiresBl) return user.isAdmin || isBlOfBereich(user, b.requiresBl);
  if (!b.requiresBereich) return true;
  const allowed = new Set(user.allowedBereiche.map((x) => x.toLowerCase()));
  return allowed.has("*") || allowed.has(b.requiresBereich.toLowerCase());
}

/** Sichtbare flache Views (für Bereiche ohne groups, z.B. Hub/DQ). */
export function visibleViewsForUser(b: BereichDef, user: UserContext): ViewDef[] {
  const flat = flattenedViews(b);
  const isBlOrAdmin = user.isAdmin || isBlVertrieb(user);
  return flat.filter((v) => isViewVisibleForUser(v, user, isBlOrAdmin));
}

/** Sichtbare Groups (Vertrieb) inkl. gefilterter Sub-Views. */
export function visibleGroupsForUser(
  b: BereichDef,
  user: UserContext
): Array<GroupDef & { views: ViewDef[] }> {
  if (!b.groups || b.groups.length === 0) return [];
  const isBlOrAdmin = user.isAdmin || isBlVertrieb(user);

  return b.groups
    .filter((g) => {
      if (g.adminOnly && !isBlOrAdmin) return false;
      if (g.requiresAktiv && g.requiresAktiv.length > 0) {
        const has = g.requiresAktiv.some((f) => user.aktiveFunktionen.includes(f));
        if (!has && !isBlOrAdmin) return false;
      }
      return true;
    })
    .map((g) => ({
      ...g,
      views: g.views.filter((v) => isViewVisibleForUser(v, user, isBlOrAdmin))
    }))
    .filter((g) => g.views.length > 0);
}

/**
 * Liefert die für den User SICHTBARE isSettings-Group eines Bereichs (inkl.
 * gefilterter Sub-Views) — oder null. Sichtbarkeit wie visibleGroupsForUser
 * (adminOnly → isBlOrAdmin). Wird vom Sub-Sidebar-Head (Zahnrad) + der
 * Einstellungs-Container-View genutzt.
 */
export function visibleSettingsGroupForUser(
  b: BereichDef,
  user: UserContext
): (GroupDef & { views: ViewDef[] }) | null {
  return visibleGroupsForUser(b, user).find((g) => g.isSettings) ?? null;
}

function isViewVisibleForUser(v: ViewDef, user: UserContext, isBlOrAdmin: boolean): boolean {
  if (v.adminOnly && !isBlOrAdmin) return false;
  if (v.requiresAktiv && v.requiresAktiv.length > 0) {
    const has = v.requiresAktiv.some((f) => user.aktiveFunktionen.includes(f));
    if (!has && !isBlOrAdmin) return false;
  }
  return true;
}

export function findBereichForRoute(path: string): BereichDef | null {
  for (const b of BEREICHE) {
    for (const v of flattenedViews(b)) {
      if (routeMatches(v.route, path)) return b;
    }
  }
  return null;
}

export function findViewForRoute(path: string): { bereich: BereichDef; view: ViewDef } | null {
  for (const b of BEREICHE) {
    for (const v of flattenedViews(b)) {
      if (routeMatches(v.route, path)) return { bereich: b, view: v };
    }
  }
  return null;
}

/** Liefert die Group eines Views (für Active-Highlight des Group-Headers). */
export function findGroupForView(b: BereichDef, viewKey: string | null): GroupDef | null {
  if (!b.groups || !viewKey) return null;
  for (const g of b.groups) {
    if (g.views.some((v) => v.key === viewKey)) return g;
  }
  return null;
}

/** Default-Sub-View einer Group (erster nicht-coming-soon View, sonst erster). */
export function defaultRouteForGroup(g: GroupDef): string {
  if (g.defaultRoute) return g.defaultRoute;
  const firstLive = g.views.find((v) => !v.comingSoon);
  if (firstLive) return firstLive.route;
  return g.views[0]?.route ?? "/";
}

function routeMatches(route: string, path: string): boolean {
  if (route === path) return true;
  if (route === "/" && path === "/hub") return true;
  if (route === "/hub" && path === "/") return true;
  return false;
}

/** True wenn User Bereichsleiter des angegebenen Bereichs ist (für Bereichs-Settings-Gating). */
export function isBlOfBereich(user: UserContext, bereich: string): boolean {
  const target = bereich.toLowerCase();
  return user.berechtigungen.some(
    (b) => b.rolle.toLowerCase() === "bereichsleiter" && b.bereich.toLowerCase() === target
  );
}

function isBlVertrieb(user: UserContext): boolean {
  return isBlOfBereich(user, "vertrieb");
}

/** True wenn User in IRGENDEINEM Bereich Bereichsleiter ist (Admin-Bereich-Sichtbarkeit 3.7). */
function isAnyBl(user: UserContext): boolean {
  return user.berechtigungen.some((b) => b.rolle.toLowerCase() === "bereichsleiter");
}
